import { safeFormatDate } from '../../utils/format-dates';
import type { ThunkAction, APIError, AppState } from '../../types/app.types';

const DATE_FORMAT = 'MMMM D, YYYY';

// Types
export type RecurringSchedule = {
  details: {
    daysSupply?: string;
    medNameId: string;
    nationalDrugCode?: string;
    quantity?: string;
    type: string;
  };
  id: string;
  accountId: string;
  isPaused: boolean;
  nextExecutionOn: string | null | undefined;
  prevExecutionOn: string | null | undefined;
};

export type RecurringSchedulesState = {
  items: Array<RecurringSchedule>;
  loading: boolean;
  initialized: boolean;
};

type GetSchedulesRequest = {
  type: 'recurring_schedule/SCHEDULES_REQUEST';
};
type GetSchedulesSuccess = {
  type: 'recurring_schedule/SCHEDULES_SUCCESS';
  data: Array<RecurringSchedule>;
};
type GetSchedulesError = {
  type: 'recurring_schedule/SCHEDULES_ERROR';
} & APIError;
type CancelScheduleSuccess = {
  type: 'recurring_schedule/CANCEL_SUCCESS';
};
type CancelScheduleError = {
  type: 'recurring_schedule/CANCEL_ERROR';
} & APIError;

export type RecurringSchedulesAction =
  | GetSchedulesRequest
  | GetSchedulesSuccess
  | GetSchedulesError
  | CancelScheduleSuccess
  | CancelScheduleError;

// Actions

export const getSchedules =
  (): ThunkAction<Promise<any>> =>
  (dispatch, getState, { blinkAPIClient }): Promise<any> => {
    dispatch({ type: 'recurring_schedule/SCHEDULES_REQUEST' });
    return blinkAPIClient
      .get(`/v1/schedules/`)
      .then((res) => res.data)
      .then((response: { schedules: Array<RecurringSchedule> }) =>
        dispatch({
          type: 'recurring_schedule/SCHEDULES_SUCCESS',
          data: response.schedules,
        })
      )
      .catch((err: APIError) =>
        dispatch({
          type: 'recurring_schedule/SCHEDULES_ERROR',
          ...err,
        })
      );
  };

export const cancelRecurringSchedule = (scheduleId: string, cancellationReason: string): ThunkAction<Promise<any>> => {
  return (dispatch, getState, { blinkAPIClient }): Promise<any> => {
    return blinkAPIClient
      .patch(`/v1/schedules/${scheduleId}/cancel`, {
        reason: cancellationReason,
      })
      .catch((err: APIError) => err);
  };
};

// Selectors
export const getScheduledMedNameIds = ({ recurringSchedules }: AppState): Array<string> =>
  recurringSchedules ? recurringSchedules.items.map((schedule) => schedule.details.medNameId) : [];
export const getScheduleWithMedNameId = (schedules: Array<RecurringSchedule>, medNameId: string) =>
  schedules.find((schedule) => schedule.details.medNameId === medNameId);

// Reducer

const initialState = {
  items: [],
  loading: false,
  initialized: false,
};

const recurringSchedulesReducer = (
  state: RecurringSchedulesState = initialState,
  action: any
): RecurringSchedulesState => {
  switch (action.type) {
    case 'recurring_schedule/SCHEDULES_REQUEST':
      return {
        ...state,
        loading: true,
      };
    case 'recurring_schedule/SCHEDULES_SUCCESS':
      return {
        items: action.data.map((schedule) => ({
          ...schedule,
          details: { ...schedule.details, medNameId: schedule.details.medNameId },
          prevExecutionOn: safeFormatDate(schedule.prevExecutionOn, DATE_FORMAT),
          nextExecutionOn: safeFormatDate(schedule.nextExecutionOn, DATE_FORMAT),
        })),
        loading: false,
        initialized: true,
      };

    default:
      return state;
  }
};

export default recurringSchedulesReducer;
